/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { api, ApiError } from "core/api";
import { Button, Card, Input, Form, Select, Switch } from "core/components";
import { navigateTo } from "core/navigation";
import { ItemCategoryFormData, ItemSubcategoryFormData, TaskClassificationFormData, TaskFormData, TaskSchema } from "core/schema";
import { admin_tasks_path, admin_task_path } from "routes";

type TaskFormProps = {
  task: TaskFormData;
  task_classifications: Array<TaskClassificationFormData>;
  item_categories: Array<ItemCategoryFormData>;
  item_subcategories: Array<ItemSubcategoryFormData>;
  item_types: Array<Record<string, boolean>>;
};

const selectGroupStyle: CSSProperties = {
  flex: 1,
  fontSize: 14,
  fontWeight: "bold",
  lineHeight: "1",
  color: "#172B4D",
  display: "inline-block",
  textAlign: "center"
};

const TaskForm = ({ task, task_classifications, item_categories, item_types }: TaskFormProps): JSX.Element => {
  const {
    control,
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { isSubmitting, errors }
  } = useForm<TaskFormData>({
    mode: "onChange",
    defaultValues: TaskSchema.cast(task),
    resolver: yupResolver(TaskSchema)
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const taskClassifications = task_classifications;
  const itemCategories = item_categories;
  const groupedItemCategories = itemCategories?.map((itemCategory) => {
    return { label: `${itemCategory.code} - ${itemCategory.name}`, itemType: itemCategory.item_type, options: itemCategory.item_subcategories };
  });
  const taskCodes = ["ADD", "CHG", "EXS", "PART", "WNTYW", "WNTYC"];

  const save = async (data: TaskFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(admin_task_path(task), data);
      } else {
        await api.post(admin_tasks_path(task), data);
      }

      navigateTo(admin_tasks_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const formatGroupLabel = (data) => {
    return (
      <div className="d-flex justify-content-between mb-2" style={selectGroupStyle}>
        <span>{data.label}</span>
        <span>{data.options.length}</span>
      </div>
    );
  };

  const filterItemSubcategories = (data, values) => {
    let filteredItemSubcategories = [];

    data.forEach((groupedItemCategory) => {
      const options = groupedItemCategory.options.filter((itemSubcategory) => values.includes(itemSubcategory.id));
      if (options.length >= 1) {
        filteredItemSubcategories = filteredItemSubcategories.concat(options);
      }
    });

    return filteredItemSubcategories;
  };

  return (
    <React.StrictMode>
      <Form errors={formErrors}>
        <Card>
          <Card.Header title={task.id ? "Edit Task" : "New Task"}>
            <Button
              icon="arrow-left"
              iconProps={{ className: "mr-3" }}
              className="button--default mr-3"
              disabled={isSubmitting}
              title="Back"
              onClick={() => navigateTo(admin_tasks_path())}
            />
            <Button
              icon="save"
              iconProps={{ className: "mr-3" }}
              className="button--brand mr-3"
              disabled={isSubmitting}
              title="Save"
              onClick={handleSubmit(async (data) => save(data))}
            />
          </Card.Header>
          <Card.Body>
            <Container>
              <Row className="px-3">
                <Col sm={1}>
                  <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        label="Active"
                        info="Active or Inactive"
                        checked={field.value}
                        value={field.value?.toString()}
                        errorMessage={errors.active?.message}
                      />
                    )}
                  />
                </Col>
              </Row>
              <Row>
                <Col lg={6} className="px-4">
                  <Controller
                    name="task_classification_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Task Classification"
                        placeholder="Select Task Classification"
                        required
                        options={taskClassifications}
                        getOptionLabel={(option) => `${option.code} - ${option.name}`}
                        getOptionValue={(option) => option.id}
                        isOptionDisabled={(option) => !option.active}
                        onChange={(newOption) => {
                          field.onChange(newOption?.id);
                        }}
                        value={taskClassifications?.find((taskClassification) => taskClassification.id == field.value) || null}
                        errorMessage={errors.task_classification_id?.message}
                        disabled={task.id != null && taskCodes.includes(task.code)}
                      />
                    )}
                  />
                  <Input
                    {...register("code")}
                    label="Code"
                    required
                    errorMessage={errors.code?.message}
                    className="field text-uppercase"
                    maxLength={10}
                    readOnly={task.id != null && taskCodes.includes(task.code)}
                  />
                  <Input {...register("name")} label="Name" required errorMessage={errors.name?.message} maxLength={100} />
                </Col>
                <Col lg={6} className="px-4">
                  <Controller
                    name="item_type"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Item Type"
                        placeholder="Select Item Type"
                        required
                        options={item_types}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        onChange={(newOption) => {
                          field.onChange(newOption?.value);
                          setValue("item_subcategories", []);
                        }}
                        value={item_types?.find((item_type) => item_type.value == field.value) || null}
                        errorMessage={errors.item_type?.message}
                        disabled={task.id != null && taskCodes.includes(task.code)}
                      />
                    )}
                  />
                  <Controller
                    name="item_subcategories"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Item Subtypes"
                        placeholder="Select Item Subtypes"
                        required
                        options={groupedItemCategories.filter((groupedItemCategory) => groupedItemCategory.itemType == getValues("item_type"))}
                        formatGroupLabel={(option) => formatGroupLabel(option)}
                        getOptionLabel={(option) => `${option.code} - ${option.name}`}
                        getOptionValue={(option) => option.id}
                        isOptionDisabled={(option) => !option.active}
                        onChange={(newOption, actionMeta) => {
                          if (actionMeta.action == "select-option") {
                            setValue("item_subcategories", [...getValues("item_subcategories"), actionMeta.option.id]);
                          } else if (actionMeta.action == "remove-value") {
                            setValue(
                              "item_subcategories",
                              getValues("item_subcategories")?.filter((value) => value != actionMeta.removedValue.id)
                            );
                          } else {
                            setValue("item_subcategories", []);
                          }
                        }}
                        errorMessage={errors.item_subcategories?.message}
                        closeMenuOnSelect={false}
                        isMulti
                        value={filterItemSubcategories(groupedItemCategories, field.value)}
                      />
                    )}
                  />
                </Col>
              </Row>
            </Container>
          </Card.Body>
        </Card>
      </Form>
    </React.StrictMode>
  );
};

export default TaskForm;
